import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import useChartTokens from "../../theme/chartTokens";
import "./HourlyBreakdownChart.css";

function HourlyBreakdownChart({ data = [], title, hourlyLimit }) {
  const chart = useChartTokens();
  const [low, normal, high] = hourlyLimit > 0
    ? [hourlyLimit * 0.25, hourlyLimit * 0.65, hourlyLimit]
    : [10, 30, 60];

  /* La demo puede ajustar los umbrales al tamaño del dispositivo. */
  const getBarColor = (consumption) => {
    if (consumption === 0) return chart.grid;
    if (consumption < low) return chart.success;
    if (consumption < normal) return chart.water;
    if (consumption < high) return chart.goal;
    return chart.over;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="ng-chart-tooltip">
          <p className="ng-chart-tooltip__label">{d.label}</p>
          <p className="ng-chart-tooltip__value">
            <span className="ng-chart-tooltip__dot" style={{ background: getBarColor(d.consumption) }} />
            Consumo <strong>{(d.consumption || 0).toFixed(2)} L</strong>
          </p>
          {d.cost !== undefined && (
            <p className="ng-chart-tooltip__value">
              <span className="ng-chart-tooltip__dot" style={{ background: chart.success }} />
              Costo <strong>${(d.cost || 0).toFixed(2)} MXN</strong>
            </p>
          )}
          {d.avgFlowRate > 0 && (
            <p className="ng-chart-tooltip__value">
              <span className="ng-chart-tooltip__dot" style={{ background: chart.water }} />
              Flujo prom. <strong>{(d.avgFlowRate || 0).toFixed(2)} L/min</strong>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const completeData = Array.from({ length: 24 }, (_, i) => {
    const existingHour = data.find((d) => d.hour === i);
    return existingHour || {
      hour: i,
      consumption: 0,
      avgFlowRate: 0,
      label: `${i.toString().padStart(2, "0")}:00`,
    };
  });

  const legend = hourlyLimit > 0 ? [
    { color: chart.success, label: `Bajo (<${Math.round(low)} L)` },
    { color: chart.water, label: `Normal (${Math.round(low)}-${Math.round(normal)} L)` },
    { color: chart.goal, label: `Alto (${Math.round(normal)}-${Math.round(high)} L)` },
    { color: chart.over, label: `Muy alto (≥${Math.round(high)} L)` },
  ] : [
    { color: chart.success, label: "Bajo (<10 L)" },
    { color: chart.water, label: "Normal (10-30 L)" },
    { color: chart.goal, label: "Alto (30-60 L)" },
    { color: chart.over, label: "Muy alto (>60 L)" },
  ];

  return (
    <div className="ng-hourly-breakdown">
      {title && <h3 className="ng-hourly-breakdown__title">{title}</h3>}
      <ResponsiveContainer width="100%" height={360}>
        <BarChart data={completeData} margin={{ top: 12, right: 16, left: 0, bottom: 40 }} barCategoryGap="22%">
          <CartesianGrid stroke={chart.grid} vertical={false} />
          <XAxis
            dataKey="label"
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fill: chart.axis, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fill: chart.axis, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={36}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: chart.grid, fillOpacity: 0.5 }} />
          <Bar dataKey="consumption" radius={[3, 3, 0, 0]} animationDuration={600} maxBarSize={28}>
            {completeData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.consumption)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="ng-hourly-breakdown__legend">
        {legend.map((item) => (
          <div key={item.label} className="ng-hourly-breakdown__legend-item">
            <span className="ng-hourly-breakdown__legend-dot" style={{ background: item.color }} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default HourlyBreakdownChart;
